import React from 'react'
import {Box} from '@chakra-ui/react'
import {Picture, Video} from '_uiComponents'
import type {VideoSource} from '_utils'

export interface ProductImageProps {
    productImage: string
    hoverImage?: string
    videoSrc?: string
    // New responsive video contract (c_cloudinaryVideo). When `sources` is non-empty the atom
    // renders one <source> per entry (no src on <video>); `videoSrc` stays as the legacy fallback.
    sources?: VideoSource[]
    videoPoster?: string
    isHorizontalTile?: boolean
    isDesktop?: boolean
    alt: string
    hasLazy?: boolean
    horizontalImage?: string
    horizontalImageAlt?: string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    styles: Record<string, any>
    // PDP carousel props (optional)
    isVisible?: boolean
    isPriority?: boolean
    // When false, Video does not receive isTileImage (no paddingTop:127.3%).
    // Defaults to true to preserve existing tile behaviour.
    isTileVideo?: boolean
}

/**
 * @name ProductImage
 *
 * @description
 * Displays a product image, hover image, or video.
 *
 * @remark
 * The component can display an image or a video based on the provided props.
 * It also supports lazy loading and different styling options for horizontal tiles and desktop views.
 *
 * @param {ProductImageProps} props - The props for the component.
 * @param {string} props.productImage - The main product image URL.
 * @param {string} props.hoverImage - The image URL to display on hover.
 * @param {string} props.videoSrc - The video URL to display instead of an image.
 * @param {boolean} props.isHorizontalTile - Boolean indicating if the layout is a horizontal tile.
 * @param {boolean} props.isDesktop - Boolean indicating if the component is being rendered on a desktop.
 * @param {string} props.alt - The alt text for the image.
 * @param {boolean} props.hasLazy - Boolean indicating if the image should be lazily loaded.
 * @param {string} props.horizontalImage - The image URL for the horizontal layout.
 * @param {string} props.horizontalImageAlt - The alt text for the horizontal image.
 * @param {Record<string, any>} props.styles - Custom styles for the component.
 *
 * @returns Rendered `ProductImage` component.
 */
export const ProductImage: React.FC<ProductImageProps> = ({
    productImage,
    hoverImage,
    videoSrc,
    sources,
    videoPoster,
    isHorizontalTile = false,
    isDesktop = false,
    alt,
    hasLazy = false,
    horizontalImage,
    horizontalImageAlt,
    styles,
    isVisible = true,
    isPriority = false,
    isTileVideo = true,
}) => {
    // Skip rendering if not visible (for PDP carousel lazy rendering)
    if (!isVisible && !isPriority) return null

    const hasParametricSources = Array.isArray(sources) && sources.length > 0

    return (
        <Box {...styles.imageWrapper}>
            {hasParametricSources ? (
                <Video
                    sources={sources}
                    poster={videoPoster}
                    autoplay
                    loop
                    isTileImage={isTileVideo}
                    position="absolute"
                    objectFit="cover"
                    alt={alt}
                    isPriority={isPriority}
                />
            ) : videoSrc ? (
                <Video videoxs={videoSrc} autoplay loop isTileImage={isTileVideo} position="absolute" objectFit="cover" alt={alt} isPriority={isPriority} />
            ) : (
                <>
                    <Picture
                        imagexs={isHorizontalTile ? horizontalImage : productImage}
                        isTileImage
                        isTileHorizontal={isHorizontalTile}
                        hasLazy={!isPriority && hasLazy}
                        position="absolute"
                        alt={isHorizontalTile ? horizontalImageAlt : alt}
                        objectFit="cover"
                        fetchpriority={isPriority ? 'high' : 'low'}
                        decoding={isPriority ? 'sync' : 'async'}
                    />
                    {hoverImage && isDesktop && !isHorizontalTile && (
                        <Box {...styles.hoverImage}>
                            <Picture imagexs={hoverImage} isTileImage position="absolute" alt={alt} objectFit="cover" />
                        </Box>
                    )}
                </>
            )}
        </Box>
    )
}
